import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined';
import {
  AppBar,
  Box,
  MenuItem,
  Select,
  Toolbar,
  type SelectChangeEvent,
  useMediaQuery,
} from '@mui/material';
import { CSSProperties, FC, useState } from 'react';
import ReactCountryFlag from 'react-country-flag';
import { useTranslation } from 'react-i18next';
import { Link, Outlet, useLocation } from 'react-router-dom';
import {
  BRAND_NAVIGATION_CLASS,
  BrandLogo,
  contrastTextColor,
  navigationCssVars,
  useBranding,
} from 'src/branding';
import MenuListComposition from 'src/components/common/hamburgerMenuMobile/hamburgerMenuMobile';
import { Circles } from 'src/components/common/Sidebar/Circles/Circles';
import { ROUTES } from 'src/components/common/Sidebar/const';
import { RegisterModal } from 'src/components/common/Sidebar/RegisterModal/RegisterModal';
import { MuiModal } from 'src/components/material/MuiModal';
import { PathRoutes } from 'src/const/Routes/PathRoutes';
import { useCircleRoleView } from 'src/hooks/CircleRoleView/useCircleRoleView';
import { useAppDispatch } from 'src/hooks/Redux/useAppDispatch';
import useAppSelector from 'src/hooks/Redux/useAppSelector';
import { useUserLoginLogout } from 'src/hooks/UserLoginLogout/UserLoginLogout';
import { setActiveTabIndex } from 'src/redux/slices/tabsSlice';
import { currentUser } from 'src/redux/slices/userSlice';
import styles from './Main.module.scss';

const LANGUAGE_STORAGE_KEY = 'webapp.language';

const MainTemplate: FC = () => {
  const [isOpenModal, setIsOpenModal] = useState(false);
  const { logout } = useUserLoginLogout();
  const { i18n, t } = useTranslation();
  const isSmallScreen = useMediaQuery('(max-width:980px)');
  const isMobile = useMediaQuery('(max-width:545px)');
  const user = useAppSelector(currentUser);
  const dispatch = useAppDispatch();
  const location = useLocation();
  const circleId = location.pathname.match(/^\/circle\/([^/]+)/)?.[1];
  const { isLeader, canToggle, toggleView } = useCircleRoleView(circleId);
  // Per-circle branding: only applied inside /circle/:id when the circle has
  // branding set. Everything below falls back to today's values otherwise.
  const branding = useBranding(circleId);
  // The top bar only takes the brand color when the circle set a primary
  // color; a circle with just a logo keeps the white Dunbar bar.
  const headerIsBranded = branding.hasHeaderSurface;
  const headerBackground = headerIsBranded ? branding.primaryColor : 'white';
  const headerOnDark =
    headerIsBranded && contrastTextColor(branding.primaryColor) === '#ffffff';
  const headerTextColor = headerIsBranded ? branding.onPrimaryColor : 'black';
  // Phone circle strip: Dunbar unless the circle asked for a branded navigation
  const circlesBarBrandClass = branding.brandNavigation
    ? ` ${styles.mobileCirclesBarBrand} ${BRAND_NAVIGATION_CLASS}`
    : '';
  const circlesBarBrandStyle = navigationCssVars(branding) as CSSProperties;

  const handleSignOut = async () => {
    await logout();
  };

  const handleToggleRoleView = () => {
    dispatch(setActiveTabIndex(0));
    toggleView();
  };

  const hideSidebar =
    location.pathname.includes('edit-circle') ||
    location.pathname.includes('create-circle') ||
    location.pathname.includes('edit-profile') ||
    location.pathname.includes('/oth/360/results') ||
    location.pathname.includes('/oth/360/form') ||
    location.pathname.includes('/oth/registration') ||
    location.pathname.includes('/oth/utviklingsplan');
  const isOth360InviteFlowRoute =
    location.pathname.includes('/oth/360/results') ||
    location.pathname.includes('/oth/360/form');
  const selectedLanguage = ['no', 'da', 'en'].includes(
    i18n.resolvedLanguage || ''
  )
    ? (i18n.resolvedLanguage as 'no' | 'da' | 'en')
    : ['no', 'da', 'en'].includes(i18n.language || '')
      ? (i18n.language as 'no' | 'da' | 'en')
      : 'no';
  const languageOptions = [
    { value: 'no', label: 'Norsk', countryCode: 'NO' },
    { value: 'da', label: 'Dansk', countryCode: 'DK' },
    { value: 'en', label: 'English', countryCode: 'GB' },
  ] as const;
  const selectedLanguageOption =
    languageOptions.find((option) => option.value === selectedLanguage) ||
    languageOptions[0];

  const marginLeft =
    hideSidebar || isMobile ? '0px' : isSmallScreen ? '57px' : '240px';
  const showMobileCircles = Boolean(isMobile && user && !hideSidebar);
  const isCircleOverviewPage = /^\/circle\/[^/]+\/?$/.test(location.pathname);
  const handleLanguageChange = (event: SelectChangeEvent<string>) => {
    const language = event.target.value;
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(LANGUAGE_STORAGE_KEY, language);
      document.documentElement.lang = language;
    }
    void i18n.changeLanguage(language);
  };

  return (
    <Box sx={{ flexGrow: 1, marginLeft }}>
      <AppBar
        color="transparent"
        position="fixed"
        sx={{
          zIndex: 10001,
          left: 0,
          boxShadow: 'none',
          borderBottom: headerIsBranded
            ? '1px solid transparent'
            : '1px solid rgba(0, 0, 0, 0.12)',
          overflow: 'visible',
        }}
      >
        <Toolbar
          sx={{
            background: headerBackground,
            display: 'flex',
            alignItems: 'center',
            ...(headerIsBranded && { color: headerTextColor }),
            ...(headerIsBranded &&
              branding.hasFont && { fontFamily: branding.fontFamily }),
          }}
        >
          <Box sx={{ flexGrow: 1, display: 'flex' }}>
            <Link to={PathRoutes.FeedPageRoute} className={styles.logoLink}>
              <BrandLogo
                branding={branding}
                className={styles.logo}
                onDark={headerOnDark}
              />
            </Link>
          </Box>
          <Box className={styles.headerActions}>
            {isMobile && isOth360InviteFlowRoute && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  color: '#111',
                  fontSize: '14px',
                  fontWeight: 700,
                  lineHeight: 1,
                  whiteSpace: 'nowrap',
                }}
              >
                <span aria-hidden="true">🌍</span>
                <span>{t('main.language.label')}:</span>
              </Box>
            )}
            {!isMobile && isOth360InviteFlowRoute && (
              <div className={styles.languageSwitcher}>
                <label
                  className={styles.languageLabel}
                  htmlFor="main-language-picker"
                >
                  <span
                    className={styles.languageLabelEmoji}
                    aria-hidden="true"
                  >
                    🌍
                  </span>
                  {t('main.language.label')}:
                </label>
                <div className={styles.languagePickerWrap}>
                  <Select
                    id="main-language-picker"
                    className={styles.languagePicker}
                    value={selectedLanguage}
                    onChange={handleLanguageChange}
                    variant="outlined"
                    inputProps={{ 'aria-label': t('main.language.label') }}
                    renderValue={() => (
                      <span className={styles.languageOption}>
                        <ReactCountryFlag
                          className={styles.languageFlag}
                          countryCode={selectedLanguageOption.countryCode}
                          svg
                          aria-hidden="true"
                        />
                        <span>{selectedLanguageOption.label}</span>
                      </span>
                    )}
                    sx={{
                      height: 48,
                      minWidth: 190,
                      color: '#111',
                      fontSize: '16px',
                      fontWeight: 700,
                      fontFamily: "'General Sans Semibold', sans-serif",
                      backgroundColor: '#fff',
                      boxShadow: '0 0 0 1px rgba(17, 17, 17, 0.08)',
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#111',
                        borderWidth: '2px',
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#111',
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#111',
                      },
                      '& .MuiSelect-select': {
                        display: 'flex',
                        alignItems: 'center',
                        padding: '10px 40px 10px 14px',
                      },
                    }}
                  >
                    {languageOptions.map((option) => (
                      <MenuItem
                        key={option.value}
                        value={option.value}
                        className={styles.languageMenuItem}
                      >
                        <span className={styles.languageOption}>
                          <ReactCountryFlag
                            className={styles.languageFlag}
                            countryCode={option.countryCode}
                            svg
                            aria-hidden="true"
                          />
                          <span>{option.label}</span>
                        </span>
                      </MenuItem>
                    ))}
                  </Select>
                </div>
              </div>
            )}
            {user ? (
              <div className={styles.profileActions}>
                {canToggle && (
                  <button
                    type="button"
                    className={styles.roleViewButton}
                    onClick={handleToggleRoleView}
                    style={
                      headerIsBranded
                        ? {
                            color: headerTextColor,
                            borderColor: headerTextColor,
                            background: 'transparent',
                          }
                        : undefined
                    }
                  >
                    View as {isLeader ? 'member' : 'leader'}
                  </button>
                )}
                {!isMobile && (
                  <LogoutOutlinedIcon
                    sx={{
                      color: headerTextColor,
                      fontSize: '25px',
                      cursor: 'pointer',
                    }}
                    onClick={handleSignOut}
                  />
                )}
                {isMobile && (
                  <MenuListComposition routes={ROUTES} isAuthenticated />
                )}
              </div>
            ) : isMobile && isOth360InviteFlowRoute ? (
              <MenuListComposition
                routes={[]}
                onLoginClick={() => setIsOpenModal(true)}
              />
            ) : (
              <button
                className={styles.btn}
                onClick={() => setIsOpenModal(true)}
                style={
                  headerIsBranded
                    ? {
                        // A branded header (the public registration page)
                        // gets an outlined button in the header text color,
                        // so the page's own main button stays the one filled
                        // action, and no Dunbar red
                        color: headerTextColor,
                        border: `1px solid ${headerTextColor}`,
                        backgroundColor: 'transparent',
                        fontFamily: 'inherit',
                        ...(branding.buttonShape === 'pill' && {
                          borderRadius: 9999,
                        }),
                      }
                    : undefined
                }
              >
                Log in
              </button>
            )}
            <MuiModal open={isOpenModal} setOpen={setIsOpenModal}>
              <RegisterModal />
            </MuiModal>
          </Box>
        </Toolbar>
        {showMobileCircles && !isCircleOverviewPage && (
          <div
            className={`${styles.mobileCirclesBar}${circlesBarBrandClass}`}
            style={circlesBarBrandStyle}
          >
            <Circles variant="horizontal" />
          </div>
        )}
      </AppBar>
      {showMobileCircles && isCircleOverviewPage && (
        <div
          className={`${styles.mobileCirclesBar} ${styles.mobileCirclesBarFlow}${circlesBarBrandClass}`}
          style={circlesBarBrandStyle}
        >
          <Circles variant="horizontal" />
        </div>
      )}
      <Outlet />
    </Box>
  );
};

export default MainTemplate;
